// Resolve a chart's shared look from the deck stylesheet (TODO §15/§6.2.3).
//
// The sibling of `stylesheet-typography.ts`, and written to the same brief: turn whatever the
// sheet happens to say into concrete CSS values, with every lookup falling back to something
// sane, because a fresh deck ships `sheets: {}` and reaches this function with nothing at all.
// Nothing downstream of `resolveChartStyle` may ask whether a field was authored.
//
// **The palette is the part worth reading twice.** §6.2.2 stores colours as NAMES into
// `sheet.colors`, never hexes, because that indirection is the entire mechanism by which one
// sheet swap restyles every grid and every chart at once. A sheet that has never had its
// palette edited names nothing — so rather than falling back to a fixed set of colours nobody
// chose, `defaultPalette` builds one out of the sheet's own colourful entries. An unstyled
// deck therefore draws a chart in its own brand colours from the first render, which is the
// same call §26 made for the gantt's accent (`resolveGanttTheme`).
//
// Fonts come out in POINTS as well as px: points are what the sheet authors, px is what
// Recharts wants, and the conversion happens here so no caller is tempted to hand a pt number
// to an SVG attribute (§8.3).

import { GRIDD_FACTORY_SHEET } from "@/lib/gridd-factory-sheet";
import {
  ptToPx,
  resolveRoleTypography,
  resolveTypography,
  sheetSwatches,
  isNeutralColor,
  swatchValue,
  type ResolvedTypography,
} from "@/lib/stylesheet-typography";
import type {
  ChartCurve,
  ChartLegendPosition,
  ChartStyleBlock,
} from "@/types/chart";
import type { GriddStyleSheetData } from "@/types/document";

export interface ResolvedChartFont extends ResolvedTypography {
  // The same size in px, converted once here (§8.3). Recharts takes numbers as px.
  fontSizePx: number;
}

export interface ResolvedChartStyle {
  // Hexes, resolved from names. Never empty — see `defaultPalette`.
  palette: string[];
  axis: { color: string; font: ResolvedChartFont };
  gridlines: { x: boolean; y: boolean; color: string; dash: string };
  legend: { position: ChartLegendPosition; font: ResolvedChartFont };
  dataLabels: { show: boolean; font: ResolvedChartFont };
  series: {
    barRadius: number;
    barGapPct: number;
    lineWidth: number;
    curve: ChartCurve;
    pointSize: number;
    areaOpacity: number;
    varyDashes: boolean;
  };
  plot: {
    // `undefined` = transparent, which is what a slide wants by default: the window already
    // has a background, and painting a second one over it shows as a seam at the corners.
    background: string | undefined;
    padding: { top: number; right: number; bottom: number; left: number };
  };
}

// §11.3 Dash patterns cycled alongside the palette when `varyDashes` is on, so two series stay
// distinguishable in a black-and-white print or to a reader who cannot separate the hues. The
// first entry is solid: the primary series is never the odd one out.
export const SERIES_DASHES = ["", "6 4", "2 3", "10 4 2 4", "1 4"];

function toChartFont(typography: ResolvedTypography): ResolvedChartFont {
  return { ...typography, fontSizePx: ptToPx(typography.fontSizePt) };
}

// A font from a named preset if the block names one, otherwise the sheet's small running
// text — a chart's axis and legend are secondary type on a slide, the same call §26/§4.4 made
// for the gantt's row labels. An explicit `fontSizePt` overrides whatever the preset says.
function resolveBlockFont(
  styleSheet: GriddStyleSheetData,
  presetName: string | undefined,
  sizePt: number | undefined,
): ResolvedChartFont {
  const base = presetName
    ? resolveTypography(styleSheet, presetName)
    : resolveRoleTypography(styleSheet, "smallBody");
  const font = toChartFont(base);
  if (sizePt === undefined || !(sizePt > 0)) return font;
  return { ...font, fontSizePt: sizePt, fontSizePx: ptToPx(sizePt) };
}

// §6.2.2 A palette out of the sheet's OWN colours when it names none of its own.
//
// Neutrals are skipped (`isNeutralColor`): a sheet lists its text, border and background
// colours next to its brand ones, and taking the list in order would draw three greys before
// reaching anything a reader can tell apart. The result is capped at eight — beyond that a
// categorical palette stops being readable and the honest answer is to cycle.
//
// The final fallback is a single mid blue, reached only by a sheet with no usable colour at
// all, which `activeSheet`'s factory fallback already makes close to impossible.
function defaultPalette(swatches: { name: string; value: string }[]): string[] {
  const colourful = swatches
    .filter((c) => c.value && !isNeutralColor(c.value))
    .map((c) => c.value)
    .slice(0, 8);
  return colourful.length > 0 ? colourful : ["#37a9ef"];
}

export function resolveChartStyle(
  styleSheet: GriddStyleSheetData,
): ResolvedChartStyle {
  const swatches = sheetSwatches(styleSheet);
  const sheet = styleSheet?.sheets?.[styleSheet?.selectedSheet];
  const block: ChartStyleBlock | undefined = sheet?.chartStyles;
  const body = resolveRoleTypography(styleSheet, "body");

  // Names → hexes, dropping any the sheet no longer defines rather than painting the literal
  // name (which resolves to nothing and draws an invisible series). An authored palette that
  // resolves to nothing falls back to the derived one, not to an empty array.
  const named = (sheet?.dataVisualizationColors?.categorical ?? [])
    .map((name) => swatchValue(swatches, name))
    .filter((hex): hex is string => Boolean(hex));

  const axisColor =
    swatchValue(swatches, block?.axis?.colorName) ??
    // Derived from the body colour rather than fixed: an axis is ink, and a deck whose text
    // is warm grey should not get a cold grey axis.
    `color-mix(in srgb, ${body.color} 55%, white)`;

  return {
    palette: named.length > 0 ? named : defaultPalette(swatches),
    axis: {
      color: axisColor,
      font: resolveBlockFont(
        styleSheet,
        block?.axis?.fontPreset,
        block?.axis?.fontSizePt,
      ),
    },
    gridlines: {
      // Horizontal rules on, vertical off: a value grid helps read a bar's height, a category
      // grid mostly adds noise. The user can turn either on.
      x: block?.gridlines?.x ?? false,
      y: block?.gridlines?.y ?? true,
      color:
        swatchValue(swatches, block?.gridlines?.colorName) ??
        `color-mix(in srgb, ${body.color} 16%, white)`,
      dash: block?.gridlines?.dash ?? "3 3",
    },
    legend: {
      position: block?.legend?.position ?? "bottom",
      font: resolveBlockFont(styleSheet, block?.legend?.fontPreset, undefined),
    },
    dataLabels: {
      show: block?.dataLabels?.show ?? false,
      font: resolveBlockFont(
        styleSheet,
        block?.dataLabels?.fontPreset,
        undefined,
      ),
    },
    series: {
      barRadius: block?.series?.barRadius ?? 3,
      barGapPct: block?.series?.barGapPct ?? 20,
      lineWidth: block?.series?.lineWidth ?? 2,
      curve: block?.series?.curve ?? "monotone",
      pointSize: block?.series?.pointSize ?? 3,
      areaOpacity: block?.series?.areaOpacity ?? 0.22,
      varyDashes: block?.series?.varyDashes ?? false,
    },
    plot: {
      background: swatchValue(swatches, block?.plot?.backgroundColorName),
      padding: block?.plot?.padding ?? {
        // §2.2 Recharts 3 requires all four; a partial `margin` is not a thing any more.
        // Left and bottom carry the axis labels, so they get the room.
        top: 8,
        right: 16,
        bottom: 8,
        left: 8,
      },
    },
  };
}

// One series' colour: its own override if it named one, otherwise its slot in the palette,
// cycling. Kept here beside the resolver so no renderer re-derives the cycle and gets the
// modulo subtly wrong on the second chart.
export function seriesColor(
  style: ResolvedChartStyle,
  swatches: { name: string; value: string }[],
  colorName: string | undefined,
  index: number,
): string {
  return (
    swatchValue(swatches, colorName) ??
    style.palette[index % style.palette.length]
  );
}

// §11.3 The dash for a series, or "" (solid) when variation is off.
export function seriesDash(style: ResolvedChartStyle, index: number): string {
  return style.series.varyDashes
    ? SERIES_DASHES[index % SERIES_DASHES.length]
    : "";
}

// === §6.3.2 THE ROUND-TRIP GUARD ===========================================================
//
// **Read this before touching the stylesheet save path.**
//
// The right panel hosts *Gridd's own* stylesheet editor, and the host commits whatever that
// editor emits straight back into `document.styleSheet` (ARCHITECTURE §6). If that editor
// rebuilds a sheet from its internal model, every key it does not know about is dropped on
// Save — so a user who styles their charts and then changes one colour in the stylesheet
// editor would silently lose every chart style, with nothing anywhere saying so.
//
// §6.3.1 asks for that to be TESTED first. §6.3.2 asks for this merge **regardless of the
// answer**, and that is the instruction followed here: the test can only ever tell us what
// today's `@gridd/embed` does, and the next release of it is not ours to control. The merge
// costs one function call on a rare action and makes the question permanently moot.
//
// What it merges is narrow on purpose — only the two keys §15 owns, per sheet by name:
//
//   • `chartStyles`             — entirely ours; Gridd has never heard of it.
//   • `dataVisualizationColors` — Gridd's own field, which `updateSheet` handles but no
//     shipped asset carries. Merged on the same terms, because "handled" is not "round-trips",
//     and an editor that emits it as `undefined` would otherwise erase the palette.
//
// Everything else in the incoming sheet wins outright. This is a backstop for keys the editor
// cannot know about, not a general "the old sheet is right" rule — using it as one would make
// the stylesheet editor unable to clear anything.
//
// The escape hatch §6.3.3 describes (a deck-level `DeckDocument.chartStyles` keyed by sheet
// name) is deliberately NOT built: it splits the design source of truth in two, and it is only
// needed if this merge proves unworkable — which it can only do if Gridd starts renaming
// sheets, since the merge is keyed on the name.
export function mergeChartStyles(
  prev: GriddStyleSheetData | undefined,
  next: GriddStyleSheetData,
): GriddStyleSheetData {
  const prevSheets = prev?.sheets;
  if (!prevSheets || !next?.sheets) return next;

  let changed = false;
  const sheets: GriddStyleSheetData["sheets"] = {};
  for (const [name, sheet] of Object.entries(next.sheets)) {
    const before = prevSheets[name];
    // A sheet the incoming payload introduces has nothing to carry forward, and one that
    // already holds both keys has nothing missing.
    const carryStyles = sheet.chartStyles === undefined && before?.chartStyles;
    const carryPalette =
      sheet.dataVisualizationColors === undefined &&
      before?.dataVisualizationColors;
    if (!carryStyles && !carryPalette) {
      sheets[name] = sheet;
      continue;
    }
    changed = true;
    sheets[name] = {
      ...sheet,
      ...(carryStyles ? { chartStyles: before.chartStyles } : {}),
      ...(carryPalette
        ? { dataVisualizationColors: before.dataVisualizationColors }
        : {}),
    };
  }
  // By reference when nothing was carried, so a commit that needed no repair does not look
  // like a change to anything comparing identities upstream.
  return changed ? { ...next, sheets } : next;
}

// Write our two keys into the SELECTED sheet, returning a whole new stylesheet.
//
// The Chart style panel edits the committed sheet directly rather than through Gridd's
// draft/commit protocol (§7.4) — those are the editor's own, and there is no way to push a
// draft of a key it does not know about into it. So this builds the next sheet and the caller
// dispatches it through `setStyleSheet` like any other commit, which is what puts it in front
// of the persistence listener and the merge above.
//
// A deck whose `sheets` is still empty gets one materialized under the selected name. That is
// the ordinary case for a deck nobody has styled yet, and refusing to store a chart style
// until the user has opened Gridd's editor once would be a strange rule to explain.
export function writeChartStyles(
  styleSheet: GriddStyleSheetData,
  patch: {
    chartStyles?: ChartStyleBlock;
    categorical?: string[];
  },
): GriddStyleSheetData {
  // `"default"` to match `withStudioPresets`, which uses that exact string when a deck names
  // no sheet. Two different fallback names would produce two sheets for one deck.
  const selectedSheet = styleSheet?.selectedSheet || "default";
  const sheets = { ...(styleSheet?.sheets ?? {}) };
  // === Seed from the FACTORY SHEET, never from `{}` ==========================================
  //
  // This is the trap in this function, and it bites the most ordinary case there is: a deck
  // nobody has styled ships `sheets: {}`, and every reader — `activeSheet` in
  // `stylesheet-typography.ts` — falls back to `GRIDD_FACTORY_SHEET`, which is what the grid
  // embeds are drawing with anyway.
  //
  // Materializing an EMPTY sheet under the selected name would end that fallback. The lookup
  // would start succeeding, and succeeding with a record that has no `colors`, no `presets`
  // and no `defaults` — so every swatch list would come back empty and all of the deck's
  // typography would collapse to `DEFAULT_TEXT_TYPOGRAPHY`. Picking one chart colour would
  // restyle every title, subtitle and text block in the deck, and nothing would say why.
  //
  // Seeding from the factory sheet is what keeps the write additive: the deck gets a real
  // sheet holding exactly what it was already being rendered with, plus the one key just set.
  const base = sheets[selectedSheet] ?? GRIDD_FACTORY_SHEET;
  sheets[selectedSheet] = {
    ...base,
    ...(patch.chartStyles !== undefined
      ? { chartStyles: patch.chartStyles }
      : {}),
    ...(patch.categorical !== undefined
      ? {
          dataVisualizationColors: {
            ...(base.dataVisualizationColors ?? { categorical: [] }),
            categorical: patch.categorical,
          },
        }
      : {}),
  };
  return { ...styleSheet, selectedSheet, sheets };
}

// The block as it currently stands in the selected sheet, defaulted so the panel can edit it
// without every control writing `version` for itself.
export function currentChartStyles(
  styleSheet: GriddStyleSheetData,
): ChartStyleBlock {
  const sheet = styleSheet?.sheets?.[styleSheet?.selectedSheet];
  return sheet?.chartStyles ?? { version: 1 };
}

// …and the authored palette NAMES (not hexes), for the same reason.
export function currentChartPalette(styleSheet: GriddStyleSheetData): string[] {
  const sheet = styleSheet?.sheets?.[styleSheet?.selectedSheet];
  return sheet?.dataVisualizationColors?.categorical ?? [];
}
